// reassign/services/reassignApi.js
// Client wrapper for /api/reassign (Reassignments v2). Design: scratch/REASSIGNMENTS-V2-DESIGN.md.
//
// Every action is gated server-side by rprs.ReassignAccess (api/shared/reassignAccess.js); the
// browser only decides what to render. Errors carry the server's message and HTTP status so a
// page can tell "no access" (403) from "bad input" (400) from an outage.

import { fetchWithColdStartRetry } from '../../scheduling/services/schedApi';

const BASE = '/api/reassign';

async function call(action, body = {}, { retry = true } = {}) {
  const url = `${BASE}?action=${encodeURIComponent(action)}`;
  const opts = {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  };
  const r = retry
    ? await fetchWithColdStartRetry(url, opts, { label: `reassign(${action})` })
    : await fetch(url, opts);

  let json = null;
  try { json = await r.json(); } catch (_) { /* non-JSON error body */ }

  if (!r.ok) {
    const err = new Error((json && json.error) || `reassign(${action}) failed (${r.status})`);
    err.status = r.status;
    throw err;
  }
  return json;
}

// ── capability probe (nav) ──────────────────────────────────────────────────
// Answers for everyone: { access: { allowed, role, canSeeV2, stage, ... } }.
export const fetchReassignAccess = () => call('my_access', {}, { retry: false });

// ── reads (viewer+) ─────────────────────────────────────────────────────────
export const fetchReassignStaff = () => call('staff');
// scope: { kind: 'staff', staffIndex } | { kind: 'clients', codes } | { kind: 'group', code } | { kind: 'office', code }
export const fetchSeats = (scope, { activeOnly = false } = {}) => call('seats', { scope, activeOnly });
export const searchGroups = (q) => call('groups', { q });
export const fetchInactiveHolders = () => call('inactive_holders');
export const fetchActivityStatus = () => call('activity_status', {}, { retry: false });

// ── admin ───────────────────────────────────────────────────────────────────
export const fetchAccessList = () => call('access_list');
export const upsertAccess = ({ staffId, role, isPreviewTester, notes }) =>
  call('access_upsert', { staffId, role, isPreviewTester: !!isPreviewTester, notes: notes || null });
export const removeAccess = (staffId) => call('access_remove', { staffId });
export const setV2Stage = (value) => call('config_set', { key: 'v2.stage', value });
export const requestActivityRefresh = ({ force = false } = {}) => call('activity_refresh', { force }, { retry: false });

// ── drafts (submitter+, v2 preview gate) ──────────────────────────────────────
// Writes never retry on a cold start: a replayed POST could create a second draft or batch.
const write = (action, body) => call(action, body, { retry: false });

export const fetchDrafts = (all = false) => call('draft_list', { all });
export const fetchDraft = (draftId) => call('draft_get', { draftId });
export const createDraft = (name, notes) => write('draft_create', { name, notes });
export const updateDraft = (draftId, patch) => write('draft_update', { draftId, ...patch });
export const deleteDraft = (draftId) => write('draft_delete', { draftId });
// batch: { source: 'one_off'|'replace'|'assign'|'split'|'import'|'align', label, cascadeMode: 'all'|'from_holder' }
// changes: [{ kind: 'seat', contIndex, role: 'CP'|'CM'|'SP'|'SM', servIndex?, toStaffIndex }
//          | { kind: 'job', jobIdx, role: 'JP'|'JM', toStaffIndex }]
// -> { batchId, added, updated, locked, autoMas, rejected: [{ i, reason }] }
export const addChanges = (draftId, batch, changes) => write('changes_add', { draftId, batch, changes });
export const removeChanges = (draftId, changeIds) => write('changes_remove', { draftId, changeIds });
export const updateBatch = (draftId, batchId, patch) => write('batch_update', { draftId, batchId, ...patch });
export const fetchDraftImpact = (draftId) => call('draft_impact', { draftId });

// ── runs ───────────────────────────────────────────────────────────────────────
// A dry run reads PE live and writes nothing (submitter+); apply writes (approver+).
export const startDryRun = (draftId) => write('draft_dry_run', { draftId });
export const applyDraft = (draftId) => write('draft_apply', { draftId });
export const fetchRunStatus = (runId) => call('run_status', { runId });
export const cancelRun = (runId) => write('run_cancel', { runId });
export const resumeRun = (runId) => write('run_resume', { runId });
